<script setup>
  import { ref, computed } from 'vue';
  import InputField from '@/components/form/InputField.vue';
  import UserService from '@/services/UserService';
  import Spinner from '@/components/Spinner.vue';
  import InlineErrorMessage from '@/components/error/InlineErrorMessage.vue';

  const props = defineProps({
    userData: {
      type: Object,
      default: () => {},
    },
    steps: {
      type: Number,
      default: 0,
    },
    validationErrors: {
      type: Object,
      default: () => {},
    },
  });
  const emit = defineEmits(['gotoStep', 'setViolations']);

  // Get terms URL from window.viploge.urls or fallback to default
  const termsUrl = computed(() => {
    return window.viploge?.urls?.terms || '/nl/voorwaarden';
  });

  const errors = ref({});
  const checking = ref(false);
  const toYoung = ref(false);
  const userExists = ref(false);

  function submitForm() {
    errors.value = {};

    if (!props.userData.agreeTerms) {
      errors.value.agreeTerms = 'Je moet akkoord gaan met de voorwaarden';
    }

    if (Object.keys(errors.value).length === 0) {
      checking.value = true;
      toYoung.value = false;
      userExists.value = false;
      UserService.checkAccount(props.userData)
        .then((res) => {
          checking.value = false;
          let error = false;
          if (res.data.toYoung) {
            error = true;
            toYoung.value = true;
          }
          if (res.data.userExists) {
            error = true;
            userExists.value = true;
          }
          if (!error) {
            emit('gotoStep', 3);
          }
        })
        .catch((err) => {
          const violations = err.response.data.violations;
          emit('setViolations', violations);
          checking.value = false;
        });
    }

    return false;
  }
</script>

<template>
  <div class="mb-4 flex items-center justify-between">
    <h1 class="text-lg font-bold text-gray-900 lg:text-xl xl:text-2xl">
      {{ $t('auth.registration.create_user_step.my_account') }}
    </h1>
    <span class="text-xs text-gray-400 lg:text-sm">(2/{{ steps }})</span>
  </div>

  <InlineErrorMessage v-if="toYoung" msg="auth.error.age" class="mb-4" />

  <form v-if="!userExists" autocomplete="off" @submit.prevent="submitForm">
    <InputField
      v-model="userData.firstName"
      label="profile.form.first_name"
      labelSrOnly
      :placeholder="$t('profile.form.first_name')"
      :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
      icon="fa-regular fa-user"
      required
    />

    <InlineErrorMessage
      v-if="validationErrors?.userData?.firstName"
      :msg="validationErrors?.userData?.firstName"
    />

    <InputField
      v-model="userData.lastName"
      class="mt-3 lg:mt-4"
      :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
      icon="fa-regular fa-user"
      label="profile.form.last_name"
      labelSrOnly
      :placeholder="$t('profile.form.last_name')"
      required
    />

    <InlineErrorMessage
      v-if="validationErrors?.userData?.lastName"
      :msg="validationErrors?.userData?.lastName"
    />

    <InputField
      v-model="userData.email"
      inputType="email"
      class="mt-3 lg:mt-4"
      :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
      icon="fa-regular fa-envelope"
      label="profile.form.email"
      labelSrOnly
      placeholder="jouw@email.be"
      required
    />

    <InlineErrorMessage
      v-if="validationErrors?.userData?.email"
      :msg="validationErrors?.userData?.email"
    />

    <InputField
      v-model="userData.password"
      class="mt-3 lg:mt-4"
      :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
      icon="fa-regular fa-lock"
      label="profile.form.password"
      labelSrOnly
      :placeholder="$t('profile.form.password')"
      inputType="password"
      required
    />

    <InlineErrorMessage
      v-if="validationErrors?.userData?.password"
      :msg="validationErrors?.userData?.password"
    />

    <div class="mt-3 flex items-center space-x-2 lg:mt-4">
      <InputField
        v-model="userData.birthdate"
        inputType="date"
        class="w-1/2"
        :inputClasses="`!bg-[#F4F6F9] !pl-10 !pr-2.5 !py-3 block w-full text-sm rounded-xl !border-gray-200 focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500`"
        icon="fa-regular fa-calendar"
        label="profile.form.birthdate"
        labelSrOnly
        :placeholder="$t('profile.form.birthdate')"
        required
      />

      <InlineErrorMessage
        v-if="validationErrors?.userData?.birthdate"
        :msg="validationErrors?.userData?.birthdate"
      />

      <div class="w-1/2">
        <label class="required sr-only">
          {{ $t('profile.form.sex') }}
        </label>
        <div class="relative">
          <i
            class="fa-regular fa-venus-mars pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
          ></i>
          <select
            v-model="userData.sex"
            required
            class="focus:!ring-primary-500 focus:!border-primary-500 block w-full appearance-none rounded-xl !border-gray-200 !bg-[#F4F6F9] !py-3 !pl-10 !pr-10 text-sm focus:!ring-1"
          >
            <option disabled value="">{{ $t('profile.form.sex') }}</option>
            <option value="v">{{ $t('profile.form.sex_female') }}</option>
            <option value="m">{{ $t('profile.form.sex_male') }}</option>
            <option value="x">{{ $t('profile.form.sex_undefined') }}</option>
          </select>
          <i
            class="fa-regular fa-chevron-down pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400"
          ></i>
        </div>
      </div>
    </div>

    <InlineErrorMessage
      v-if="validationErrors?.userData?.sex"
      :msg="validationErrors?.userData?.sex"
    />

    <div class="mt-2 flex items-center lg:mt-3">
      <input
        id="agreeTerms"
        v-model="userData.agreeTerms"
        type="checkbox"
        class="size-4 rounded border-gray-300 text-stone-600 focus:ring-stone-500"
        :class="{
          'border-red-300 text-red-600 focus:ring-red-500': errors.agreeTerms,
        }"
      />

      <label
        for="agreeTerms"
        class="ml-2 block text-sm leading-5 text-gray-400"
        :class="{
          'text-red-600': errors.agreeTerms,
        }"
      >
        {{ $t('auth.agree_to') }}
      </label>

      <a
        :href="termsUrl"
        target="_blank"
        class="ml-2 text-sm leading-5 underline"
        :class="[
          {
            'text-red-600': errors.agreeTerms,
          },
          {
            'hover:text-primary-600 text-gray-400': !errors.agreeTerms,
          },
        ]"
      >
        {{ $t('auth.terms') }}
      </a>
    </div>

    <button
      class="btn-primary mx-auto mt-8 flex w-full items-center justify-center !rounded-full !px-5 !py-3 !text-center text-sm lg:mt-5 xl:mx-0 xl:w-auto"
      type="submit"
      :disabled="checking"
    >
      <span>
        {{ $t('auth.registration.create_user_step.provide_contact_details') }}
      </span>
      <span v-if="checking">
        <Spinner class="size-5 text-white" />
      </span>
      <span v-else><i class="fa-regular fa-arrow-right"></i></span>
    </button>
  </form>

  <div v-else class="pt-6">
    <div class="mt-3 rounded bg-red-400 p-3 text-sm text-white lg:mt-5 lg:p-4">
      {{ $t('auth.registration.create_user_step.account_already_exists') }}
    </div>
    <a class="text-primary-600 mt-3 block underline" href="/login">
      {{ $t('auth.registration.create_user_step.try_logging_in') }}
    </a>
  </div>
</template>
